import { useEffect, useRef, useState } from "react";
import { useDemoIdentity } from "../../../lib/DemoIdentityContext";
import { useTenantTerms } from "../../../lib/TenantConfigContext";
import { Btn, CARD, cx } from "../../../components/msd/ui";
import { Icon } from "../../../components/msd/Icon";

// Pieces the brand's Organization, People and Roles pages share.

/** Per page: whether this login may open it, and whether it may change it.
 *  `edit` is exactly what the old pages' readOnly flag said. */
export function useOrgAccess() {
  const { role } = useDemoIdentity();
  const { pageLevel, canSeePage } = useTenantTerms();
  const of = (page) => {
    const edit = pageLevel(role, page) === "edit";
    return { see: edit || !!canSeePage(role, page), edit };
  };
  return {
    regions: of("regions"),
    dealers: of("dealers"),
    workshops: of("workshops"),
    people: of("people"),
    roles: of("roles"),
  };
}

// .gico — the small coloured square an icon sits in
export function Gico({
  icon,
  size = 30,
  radius = 8,
  iconSize = 15,
  bg,
  fg = "#fff",
}) {
  return (
    <span
      className="flex shrink-0 items-center justify-center"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: bg,
        color: fg,
      }}
    >
      <Icon name={icon} size={iconSize} stroke={2} />
    </span>
  );
}

// .tg-list / .opt-list — rows in one rounded, raised list
export const TG_LIST =
  "flex flex-col overflow-hidden rounded-[14px] shadow-msd-e1 [&>*+*]:shadow-[0_-1px_0_var(--line)]";
export const TG_ROW = "flex items-center gap-3 bg-msd-surface px-[14px] py-3";

// The two-pane pages (Roles and access, Organization): a list on the left, the chosen
// item edited on the right. Both fill the window under the title and scroll on their
// own; narrow screens stack them.
export const PANES =
  "grid h-[calc(100dvh-212px)] min-h-[420px] grid-cols-[300px_minmax(0,1fr)] gap-4 max-[900px]:h-auto max-[900px]:grid-cols-1";
export const PANE_NAV = cx(CARD, "overflow-y-auto p-3");
export const PANE_BODY = cx(
  CARD,
  "min-w-0 overflow-y-auto max-[900px]:overflow-visible",
);
export const PANE_HEAD =
  "sticky top-0 z-20 flex min-h-[64px] flex-wrap items-center gap-2 rounded-t-[20px] bg-msd-surface px-6 py-3 shadow-[0_1px_0_var(--line)]";
export const PANE_SECTION =
  "flex flex-col gap-3 border-t border-msd-line px-6 py-5 first:border-t-0";
export const PANE_TITLE = "m-0 text-[14px] font-semibold text-msd-ink";
// A phone-settings group: rounded grey card, one row per item, a line between rows.
export const SETTINGS_GROUP = "rounded-[12px] bg-msd-ground";
export const SETTINGS_ROW =
  "flex min-h-[46px] items-center gap-3 border-t border-msd-line px-4 py-[6px] first:border-t-0";
// The page's inputs are sized for a form on its own; these sit in a dense editor.
export const SMALL_INPUT =
  "h-9 w-full min-w-0 rounded-[10px] border-0 bg-msd-ground px-3 text-[14px] text-msd-ink outline-none placeholder:text-msd-faint focus:shadow-[0_0_0_2px_#3A5F94] disabled:opacity-60";

/** A .fl for a group of controls. Field is a <label>, which would hand a click
 *  anywhere in it to the first switch, so a list of them needs a plain box. */
export function FieldGroup({ label, error, hint, children }) {
  return (
    <div className="flex flex-col gap-[7px]" role="group" aria-label={label}>
      <span className="text-[13.5px] font-semibold text-msd-ink2">{label}</span>
      {children}
      {error ? (
        <span className="text-[12.5px] text-msd-brick-ink" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="text-[12.5px] text-msd-muted">{hint}</span>
      ) : null}
    </div>
  );
}

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function OrgLoading() {
  return <div className="px-1 py-6 text-[14px] text-msd-muted">Loading</div>;
}

/** A query that failed says so, with a retry, rather than spinning forever. */
export function OrgError({ error, onRetry }) {
  const detail =
    error?.response?.data?.detail || error?.message || "Something went wrong.";
  return (
    <div className={cx(CARD, "px-8 py-12 text-center")}>
      <div className="text-[16px] font-bold text-msd-ink">
        Couldn&apos;t load this
      </div>
      <p className="mx-auto mb-0 mt-2 max-w-[460px] text-[13px] text-msd-muted">
        {detail}
      </p>
      {onRetry ? (
        <Btn className="mt-[18px]" onClick={onRetry}>
          Try again
        </Btn>
      ) : null}
    </div>
  );
}

/** Confirmation before a delete. `blocked` turns it into an explanation of
 *  why it cannot happen yet, with no destructive button to press. Rendered in
 *  place (not portalled) so it keeps the `.msd` tokens. */
export function ConfirmDelete({
  open,
  onClose,
  name,
  body,
  blocked,
  onConfirm,
  pending,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [open, onClose]);
  if (!open) return null;
  const title = blocked ? `Can't delete ${name}` : `Delete ${name}?`;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[rgba(15,21,48,.18)]"
        onClick={onClose}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex w-full max-w-[420px] flex-col gap-3 rounded-[22px] bg-msd-surface p-[22px] text-msd-ink shadow-msd-pop"
      >
        <div className="text-[20px] font-bold tracking-[-0.015em]">{title}</div>
        <p className="m-0 text-[14px] text-msd-ink2">{blocked || body}</p>
        <div className="mt-2 flex justify-end gap-[10px]">
          <Btn onClick={onClose}>{blocked ? "Close" : "Cancel"}</Btn>
          {!blocked && (
            <Btn variant="red" onClick={onConfirm} disabled={pending}>
              Delete
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

/** The red text button on the left of a sheet's footer. */
export function RemoveBtn({ children, ...rest }) {
  return (
    <Btn className="mr-auto !text-[#F04438]" {...rest}>
      {children}
    </Btn>
  );
}

/** A choice menu: the current choice in grey (dark once granted) with a chevron, and a
 *  small menu of the choices under it. Rendered in place, not portalled, so it keeps the
 *  page's colours. Arrow keys move, Enter picks, Escape or a click elsewhere closes.
 *  `field` dresses the trigger as an input, for a choice that is not a settings row. */
export function Picker({
  options,
  value,
  onChange,
  label,
  disabled,
  field = false,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const current = options.find(([k]) => k === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => !box.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const show = () => {
    setActive(
      Math.max(
        0,
        options.findIndex(([k]) => k === value),
      ),
    );
    setOpen(true);
  };
  const pick = (k) => {
    setOpen(false);
    if (k !== value) onChange(k);
  };
  const onKey = (e) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        show();
      }
      return;
    }
    if (e.key === "Escape" || e.key === "Tab") setOpen(false);
    else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive(
        (a) =>
          (a + (e.key === "ArrowDown" ? 1 : options.length - 1)) %
          options.length,
      );
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(options[active][0]);
    }
  };

  if (disabled) {
    return (
      <span
        className={cx(
          "text-[14px]",
          value === "none" ? "text-msd-muted" : "text-msd-ink",
        )}
      >
        {current[1]}
      </span>
    );
  }
  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current[1]}`}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKey}
        className={cx(
          "flex h-8 cursor-pointer items-center gap-1 rounded-[8px] border-0 bg-transparent pl-2 pr-1 text-[14px] hover:bg-msd-soft",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A5F94]",
          field
            ? "h-9 w-full justify-between bg-msd-ground px-3 text-msd-ink hover:bg-msd-ground"
            : value === "none"
              ? "text-msd-muted"
              : "font-medium text-msd-ink",
        )}
      >
        {current[1]}
        <Icon name="down" size={14} stroke={2} color="var(--muted)" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={label}
          className={cx(
            "absolute top-[calc(100%+4px)] z-30 m-0 min-w-[150px] list-none rounded-[12px] bg-msd-surface p-1 shadow-msd-pop",
            field ? "left-0 w-full" : "right-0",
          )}
        >
          {options.map(([k, text], n) => (
            <li
              key={k}
              role="option"
              aria-selected={k === value}
              onMouseEnter={() => setActive(n)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(k)}
              className={cx(
                "flex cursor-pointer items-center gap-2 rounded-[8px] px-[10px] py-[7px] text-[14px] text-msd-ink",
                n === active && "bg-msd-soft",
              )}
            >
              <span className="grow">{text}</span>
              {k === value && <Icon name="check" size={15} stroke={2.4} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
